import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import { useCreateProgram, useProgramTypes } from '../../../api/hooks/program.hooks';
import { useOrganization } from '../../../contexts/OrganizationContext';

interface CreateProgramModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (programId: string) => void;
}

const CreateProgramModal: React.FC<CreateProgramModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [id, setId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [programTypeId, setProgramTypeId] = useState<string>('');

  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateProgram(selectedOrganization?.id || '');
  const { data: programTypes = [] } = useProgramTypes(selectedOrganization?.id);

  useEffect(() => {
    if (open) {
      setId(uuidv4());
      setProgramTypeId('');
    } else {
      setId(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    if (!selectedOrganization?.id) {
      enqueueSnackbar('No hay organización seleccionada', { variant: 'error' });
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        ...(id && { id }),
        name: name.trim(),
        description: description.trim() || "",
        organizationId: selectedOrganization.id,
        ...(programTypeId ? { programTypeId } : {}),
        isActive: true,
      });

      enqueueSnackbar('Programa creado exitosamente', { variant: 'success' });
      handleClose();
      if (onSuccess && result.id) {
        onSuccess(result.id);
      }
    } catch (error) {
      enqueueSnackbar('Error al crear el programa', { variant: 'error' });
      console.error('Error creating program:', error);
    }
  };

  const handleClose = () => {
    setId(null);
    setName('');
    setDescription('');
    setProgramTypeId('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear Nuevo Programa</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Tipo de Programa</InputLabel>
            <Select
              value={programTypeId}
              onChange={(e) => setProgramTypeId(e.target.value)}
              label="Tipo de Programa"
            >
              <MenuItem value="">
                <em>Sin tipo</em>
              </MenuItem>
              {programTypes.map((pt) => (
                <MenuItem key={pt.id} value={pt.id}>
                  {pt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createMutation.isPending}
          startIcon={createMutation.isPending && <CircularProgress size={20} />}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProgramModal;

